import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { Theme } from "@mui/material/styles";

import {
  staffCalendarOffDaySx,
  staffCalendarStatusSymbol,
  staffCalendarStatusSx,
  type StaffCalendarCellTone,
} from "../shared/staff-calendar-status-tokens";

import type {
  AttendanceDayCell,
  AttendanceDayStatusKind,
  AttendanceDensity,
} from "./attendance-types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const ATTENDANCE_EXCEPTION_SOFT_THRESHOLD = 3;

/** Format an Instant as local `HH:mm` in the tenant timezone. */
export function formatAttendanceTimeLocal(
  iso: string | null | undefined,
  timezoneName: string,
  empty = "—"
): string {
  if (iso == null || iso === "") return empty;
  const d = dayjs(iso).tz(timezoneName);
  return d.isValid() ? d.format("HH:mm") : empty;
}

function attendanceStatusTokenId(
  status: AttendanceDayStatusKind
): Parameters<typeof staffCalendarStatusSymbol>[0] | null {
  switch (status) {
    case "PRESENT":
      return "present";
    case "INCOMPLETE":
      return "incomplete";
    case "ABSENT":
      return "absent";
    case "OFF":
      return "offDay";
    case "HOLIDAY":
      return "holiday";
    case "LEAVE":
      return "approvedLeave";
    case "LEAVE_PENDING":
      return "pendingLeave";
    default:
      return null;
  }
}

export function compactStatusSymbol(status: AttendanceDayStatusKind): string {
  const id = attendanceStatusTokenId(status);
  return id ? staffCalendarStatusSymbol(id, "attendance") : "?";
}

/** Day duration as `Xh Ym` (e.g. `8h 30m`); null/undefined → empty placeholder. */
export function formatAttendanceHoursLabel(hours: number | null | undefined, empty = "—"): string {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return empty;
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0 && m === 0) return "0h";
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Cell label for grid density modes. */
export function formatAttendanceCellLabel(
  cell: AttendanceDayCell | undefined,
  density: AttendanceDensity,
  timezoneName: string
): string {
  if (!cell) return density === "compact" ? "X" : "X";

  if (cell.status === "LEAVE") {
    return density === "compact" ? "L" : "Approved leave";
  }
  if (cell.status === "LEAVE_PENDING") {
    return density === "compact" ? "Lp" : "Pending leave";
  }
  if (cell.status === "HOLIDAY") {
    return density === "compact" ? "H" : "Holiday";
  }

  if (density === "compact") {
    return compactStatusSymbol(cell.status);
  }

  if (cell.status === "OFF") {
    if (cell.punches.length === 0) return "—";
    const inT = formatAttendanceTimeLocal(cell.checkIn, timezoneName, "-");
    const outT = formatAttendanceTimeLocal(cell.checkOut, timezoneName, "-");
    if (inT === outT) return `${inT}`;
    return `${inT}–${outT}`;
  }

  if (cell.status === "ABSENT") {
    return "X";
  }

  const inT = formatAttendanceTimeLocal(cell.checkIn, timezoneName, "-");
  const outT = formatAttendanceTimeLocal(cell.checkOut, timezoneName, "-");

  if (cell.status === "INCOMPLETE") {
    return `${inT}–X`;
  }

  return `${inT}–${outT}`;
}

export type AttendanceCellTone = StaffCalendarCellTone;

/** Re-export for callers that styled off columns before shared tokens existed. */
export function attendanceOffDaySx(theme: Theme): AttendanceCellTone {
  return staffCalendarOffDaySx(theme);
}

/** Theme-token cell styles (no raw hex). */
export function attendanceCellSx(
  theme: Theme,
  cell: AttendanceDayCell | undefined,
  options?: { isOffColumn?: boolean }
): AttendanceCellTone {
  const status = cell?.status;
  const isOff = status === "OFF" || status === "HOLIDAY" || options?.isOffColumn;

  if (isOff && (!status || status === "OFF" || status === "HOLIDAY")) {
    return staffCalendarOffDaySx(theme);
  }

  const tokenId = status ? attendanceStatusTokenId(status) : null;
  if (!tokenId) return {};

  if (tokenId === "present" && cell?.late) {
    return { ...staffCalendarStatusSx(theme, tokenId), fontWeight: 700 };
  }

  return staffCalendarStatusSx(theme, tokenId);
}

export function isExceptionRow(absentDays: number, incompleteDays: number): boolean {
  return absentDays + incompleteDays > ATTENDANCE_EXCEPTION_SOFT_THRESHOLD;
}

export function isOffWeekday(weekday: number, offWeekdays: number[]): boolean {
  return offWeekdays.includes(weekday);
}
